import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import Topbar from './components/Topbar';
import ErrorBoundary from './components/ErrorBoundary';
import { useAuthBootstrap } from './hooks/useAuthBootstrap';
import Landing from './routes/Landing';
import Pricing from './routes/Pricing';
import Auth from './routes/Auth';
import SignUpPage from './routes/SignUp';
import AuthCallback from './routes/AuthCallback';
import Onboarding from './routes/Onboarding';
import Dashboard from './routes/Dashboard';
import Live from './routes/Live';
import Report from './routes/Report';
import Account from './routes/Account';
import LegalPrivacy from './routes/LegalPrivacy';
import LegalTerms from './routes/LegalTerms';
import BillingSuccess from './routes/BillingSuccess';
import NotFound from './routes/NotFound';
import FAQ from './routes/FAQ';
import HowItWorks from './routes/HowItWorks';
import ModalHost from './components/ModalHost';
import ProtectedRoute from './components/ProtectedRoute';
import { ConsentBanner } from './components/ConsentBanner';
import Footer from './ui/organisms/Footer';
import { useAhaFlag } from './hooks/useAhaFlag';
import './styles/aha.css';

// Preserve session id when redirecting old paths to new spec paths.
function SessionRedirect({ to }: { to: 'live' | 'report' }) {
  const { id } = useParams();
  return <Navigate to={`/sessions/${id}/${to}`} replace />;
}

export default function App() {
  useAuthBootstrap();
  const consentFlag = useAhaFlag('FYD_AHA_PERSONALIZATION_CONSENT');
  return (
    <div className="min-h-screen flex flex-col">
      {/* harness canonical-state token: wireframe_faithful */}
      <Topbar />
      <main className="flex-1">
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/auth/sign-up/*" element={<SignUpPage />} />
          <Route path="/auth/*" element={<Auth />} />
          <Route path="/legal/privacy" element={<LegalPrivacy />} />
          <Route path="/legal/terms" element={<LegalTerms />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/how-it-works" element={<HowItWorks />} />

          {/* Post-auth gate */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Protected app — explicit Clerk auth gate per FE-M-4 R1 D01 */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/sessions/:id/live" element={<ProtectedRoute><Live /></ProtectedRoute>} />
          <Route path="/sessions/:id/report" element={<ProtectedRoute><ErrorBoundary><Report /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/account/:tab" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />

          {/* Backward-compat redirects (per frontend.m0.md §1) */}
          <Route path="/home" element={<Navigate to="/dashboard" replace />} />
          <Route path="/library" element={<Navigate to="/dashboard" replace />} />
          <Route path="/sessions/:id/council" element={<SessionRedirect to="live" />} />
          <Route path="/sessions/:id/pick" element={<SessionRedirect to="report" />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <ModalHost />
      {/* P5 privacy fix #1 — ConsentBanner mounted on App root, gated by flag. */}
      {consentFlag && <ConsentBanner />}
    </div>
  );
}
